import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { from, switchMap, catchError, throwError } from 'rxjs';
import { SSOService } from '../../services/sso.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const ssoService = inject(SSOService);
  const router = inject(Router);

  // Check for traditional token first
  const token = localStorage.getItem('token');

  if (token && token.trim() !== '') {
    console.log('AuthInterceptor: Using traditional token');
    const authReq = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
    return next(authReq).pipe(
      catchError(error => {
        if (error.status === 401) {
          console.warn('AuthInterceptor: 401 error with traditional token, clearing and redirecting to login');
          localStorage.removeItem('token');
          router.navigateByUrl('/login');
        }
        return throwError(() => error);
      })
    );
  }

  // Try to get cached SSO token first for better performance
  const cachedToken = ssoService.getCachedToken();
  if (cachedToken) {
    console.log('AuthInterceptor: Using cached SSO token');
    const authReq = req.clone({ setHeaders: { Authorization: `Bearer ${cachedToken}` } });
    return next(authReq).pipe(
      catchError(error => {
        if (error.status === 401) {
          console.warn('AuthInterceptor: 401 error with cached token, refreshing token');
          // Token might be expired, try to refresh it
          return from(ssoService.getAccessToken()).pipe(
            switchMap(freshToken => {
              if (freshToken && freshToken.trim() !== '') {
                console.log('AuthInterceptor: Retrying with fresh token');
                const retryReq = req.clone({ setHeaders: { Authorization: `Bearer ${freshToken}` } });
                return next(retryReq);
              } else {
                console.warn('AuthInterceptor: Could not get fresh token, logging out');
                ssoService.logout();
                return throwError(() => error);
              }
            }),
            catchError(() => {
              ssoService.logout();
              return throwError(() => error);
            })
          );
        }
        return throwError(() => error);
      })
    );
  }

  // No cached token, try to get fresh token (might make network call)
  console.log('AuthInterceptor: No cached token, attempting to get fresh SSO token');
  return from(ssoService.getAccessToken()).pipe(
    switchMap(ssoToken => {
      if (ssoToken && ssoToken.trim() !== '') {
        console.log('AuthInterceptor: Using fresh SSO token');
        const authReq = req.clone({ setHeaders: { Authorization: `Bearer ${ssoToken}` } });
        return next(authReq).pipe(
          catchError(error => {
            if (error.status === 401) {
              console.warn('AuthInterceptor: 401 error with fresh SSO token, logging out');
              ssoService.logout();
            }
            return throwError(() => error);
          })
        );
      }
      // No token available, proceed without authorization header
      console.warn('AuthInterceptor: No valid token available for request to:', req.url);
      return next(req).pipe(
        catchError(error => {
          if (error.status === 401) {
            console.warn('AuthInterceptor: 401 error without token, redirecting to login');
            router.navigateByUrl('/login');
          }
          return throwError(() => error);
        })
      );
    }),
    catchError(tokenError => {
      console.error('AuthInterceptor: Failed to get token:', tokenError);
      return next(req).pipe(
        catchError(error => {
          if (error.status === 401) {
            console.warn('AuthInterceptor: 401 error after token failure, redirecting to login');
            router.navigateByUrl('/login');
          }
          return throwError(() => error);
        })
      );
    })
  );
};
